import { useState, useEffect, useMemo } from "react";
import { X } from "lucide-react";
import { Reprint } from "./Print/ReprintBill";
import store from "../store";

const getOrderStatus = (status) => {
  switch (status) {
    case "settle_later":
      return "Pending";
    case "pending":
      return "Live";
    case "paid":
      return "Paid";
    default:
      return status;
  }
};

const getOredrStatusColor = (status) => {
  switch (status) {
    case "settle_later":
      return "bg-yellow-100 text-yellow-800";
    case "pending":
      return "bg-[#FFA500]  text-white";
    case "cancelled":
      return "bg-red-100 text-red-800";
    default:
      return "bg-green-100 text-green-800";
  }
};

const BillDetailsModal = ({ selectedBill, setIsViewBillOpen }) => {
  const [isAnimating, setIsAnimating] = useState(true);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        handleClose();
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [setIsViewBillOpen]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsAnimating(false);
    }, 50);
    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    setIsAnimating(true);
    setTimeout(() => {
      setIsViewBillOpen(false);
    }, 300);
  };

  const handleReceiptPrint = async () => {
    const order = selectedBill;
    const tableDetails = store.rooms.reduce(
      (acc, room) => {
        const table = room.tables.find(
          (table) => table.qrpidVPA === order.tableVPA
        );
        return table
          ? { roomName: room.roomName, tableNo: table.tableNo }
          : acc;
      },
      { roomName: "", tableNo: "" }
    );
    await Reprint({
      ...order,
      billStatus: getOrderStatus(order.billStatus),
      tableDetails,
    });
  };

  const { roundOff, netAmount } = useMemo(() => {
    const {
      subTotal,
      discount,
      gst,
      gstExcluded,
      cgst = 0,
      sgst = 0,
      tip,
      total,
      service,
      serviceAmount = 0,
    } = selectedBill.billTxnLogs;

    // Calculate total discount including coupon if present
    let totalDiscount = discount;
    const couponValue = selectedBill?.couponMetadata?.couponValue || 0;

    let netAmount = Number(subTotal - totalDiscount + tip);
    if (gst && gstExcluded) {
      netAmount += cgst + sgst;
    }
    if (service && serviceAmount > 0) {
      netAmount += Number(serviceAmount);
    }
    if (couponValue > 0) {
      netAmount -= Number(couponValue);
    }
    const roundOff = total - netAmount;
    return {
      roundOff: roundOff.toFixed(2),
      netAmount: netAmount.toFixed(2),
    };
  }, [selectedBill]);

  console.log(
  "Selected Bill Items:",
  JSON.stringify(selectedBill?.items, null, 2)
);

  return (
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black transition-opacity duration-300 ${
          isAnimating ? "bg-opacity-0" : "bg-opacity-50"
        }`}
        onClick={handleClose}
      />

      {/* Drawer */}
      <div
        className={`fixed inset-y-0 right-0 w-full sm:w-[40%] bg-white shadow-xl transform transition-transform duration-300 ease-out ${
          isAnimating ? "translate-x-full" : "translate-x-0"
        }`}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white px-6 py-4 border-b border-gray-300">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold">
                Bill No #{selectedBill.billNo}
              </h1>
              <p className="text-gray-500 text-sm">
                {new Date(selectedBill.createdAt).toLocaleString()}
              </p>
            </div>
            <button
              className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none"
              onClick={handleClose}
            >
              <span className="sr-only">Close panel</span>
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto h-[calc(100vh-140px)] px-6 py-4">
          {/* Status & Payment Info */}
          <div className="flex justify-between items-center mb-4 text-sm">
            <span
              className={`px-3 py-1 rounded-full font-medium ${getOredrStatusColor(
                selectedBill.billStatus
              )}`}
            >
              {getOrderStatus(selectedBill.billStatus)}
            </span>
            <span className="text-gray-600 capitalize">
              {selectedBill?.paymentmethod}
            </span>
          </div>

          {selectedBill.canceled && (
            <div className="mb-4 bg-red-50 border border-red-100 p-2 rounded text-sm">
              <span className="text-red-800 font-medium">CANCELLED</span>
              <span className="text-red-600 ml-2">
                Reason: {selectedBill?.cancelReason || "Not specified"}
              </span>
            </div>
          )}

          {/* Order Items */}
          <div className="mb-4">
            <div className="bg-gray-50 rounded overflow-hidden">
              <div className="grid grid-cols-12 gap-2 bg-gray-100 p-2 text-xs font-medium text-gray-700">
                <div className="col-span-4">Item</div>
                <div className="col-span-2">Variant</div>
                <div className="col-span-2 text-center">Qty</div>
                <div className="col-span-2 text-right">Price</div>
                <div className="col-span-2 text-right">Final Amount</div>
              </div>

              <div className="divide-y divide-gray-100">
                <div className="divide-y divide-gray-100">
  {selectedBill?.items?.map((item, index) => {
    const addonsList =
      item?.addOns ||
      item?.addOn ||
      item?.addons ||
      item?.selectedAddOns ||
      [];

    const quantity = Number(item?.quantity || 1);
    const itemPrice = Number(item?.price || 0);
    const discount = Number(item?.discountAmount || 0);

    const addonTotal = addonsList.reduce(
      (sum, addon) => sum + Number(addon?.price || 0),
      0
    );

    const finalPrice =
      (itemPrice - discount + addonTotal) * quantity;

    return (
      <div
        key={index}
        className="p-3 text-sm"
      >
        <div className="grid grid-cols-12 gap-2 items-start">

          {/* Item */}
          <div className="col-span-5">

            <div className="font-semibold text-gray-800">
              {item?.name || "Unknown Item"}
            </div>

            {item?.variantName &&
            item?.variantName !== "NA" && (
              <div className="text-xs text-gray-500 mt-1">
                {item.variantName}
              </div>
            )}

            {item?.isCancelled && (
              <div className="text-xs text-red-500">
                Cancelled
              </div>
            )}

            {/* Add-ons section */}
            {addonsList.length > 0 && (
              <div className="mt-2 ml-2 border-l-2 border-gray-200 pl-2">

                <div className="text-[11px] font-semibold text-gray-400 mb-1">
                  ADD-ONS
                </div>

                {addonsList.map((addon, idx) => (
                  <div
                    key={idx}
                    className="flex justify-between text-xs py-1"
                  >
                    <span className="text-gray-600">
                      • {addon.name}
                    </span>

                    <span className="font-medium text-blue-600">
                      +₹{addon.price}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Quantity */}
          <div className="col-span-2 text-center">
            {quantity}
          </div>

          {/* Base price */}
          <div className="col-span-2 text-right">
            {discount > 0 ? (
              <div>
                <div className="font-medium">
                  ₹{(itemPrice-discount).toFixed(2)}
                </div>

                <div className="text-xs text-gray-400 line-through">
                  ₹{itemPrice.toFixed(2)}
                </div>
              </div>
            ) : (
              <div className="font-medium">
                ₹{itemPrice.toFixed(2)}
              </div>
            )}
          </div>

          {/* Final amount */}
          <div className="col-span-3 text-right">

            <div className="font-bold text-gray-900">
              ₹{finalPrice.toFixed(2)}
            </div>

            {addonTotal > 0 && (
              <div className="text-xs text-blue-600">
                Add-ons +₹{addonTotal}
              </div>
            )}
          </div>

        </div>
      </div>
    );
  })}
</div>
              </div>
            </div>
          </div>

          {/* Transaction Details */}
          <div className="text-sm">
            <div className="grid grid-cols-2 gap-x-4 gap-y-2">
              {/* Subtotal Section */}
              <div className="col-span-2 font-medium border-b pb-1 mb-1">
                Sub Total
              </div>
              <div className="text-gray-600">Sub Total Amount</div>
              <div className="text-right">
                ₹{selectedBill?.billTxnLogs?.subTotal || 0}
              </div>

              {/* Discounts Section */}
              {(selectedBill?.billTxnLogs?.discount > 0 ||
                selectedBill?.couponMetadata) && (
                <>
                  <div className="col-span-2 font-medium border-b pb-1 mb-1 mt-3">
                    Discounts
                  </div>

                  {selectedBill?.billTxnLogs?.discount > 0 && (
                    <>
                      <div className="text-gray-600">Discount Amount</div>
                      <div className="text-right text-green-600">
                        -₹{selectedBill?.billTxnLogs?.discount.toFixed(2)}
                      </div>

                      <div className="text-gray-600">Flat Discount</div>
                      <div className="text-right">
                        ₹
                        {selectedBill?.billTxnLogs?.discountFlat.toFixed(2) ||
                          0}
                      </div>

                      <div className="text-gray-600">Discount Percentage</div>
                      <div className="text-right">
                        {selectedBill?.billTxnLogs?.discountPercent.toFixed(
                          2
                        ) || 0}
                        %
                      </div>
                    </>
                  )}

                  {/* Coupon Details */}
                  {selectedBill?.couponMetadata &&
                    selectedBill?.couponMetadata !== "NA" && (
                      <>
                        <div className="text-gray-600">
                          Coupon ({selectedBill.couponMetadata.couponCode})
                        </div>
                        <div className="text-right text-green-600">
                          -{selectedBill.couponMetadata.couponValue}
                        </div>
                      </>
                    )}
                </>
              )}

              {/* Taxes Section */}
              {selectedBill?.billTxnLogs?.gst && (
                <>
                  <div className="col-span-2 font-medium border-b pb-1 mb-1 mt-3">
                    Taxes
                  </div>
                  <div className="text-gray-600">GST Number</div>
                  <div className="text-right">
                    {selectedBill?.billTxnLogs?.gstNo || "N/A"}
                  </div>

                  <div className="text-gray-600">GST Type</div>
                  <div className="text-right">
                    {selectedBill?.billTxnLogs?.gstExcluded
                      ? "Excluded"
                      : "Included"}
                  </div>

                  <div className="text-gray-600">
                    CGST ({selectedBill?.billTxnLogs?.cgstPercent || 0}%)
                  </div>
                  <div className="text-right">
                    ₹{selectedBill?.billTxnLogs?.cgst || 0}
                  </div>

                  <div className="text-gray-600">
                    SGST ({selectedBill?.billTxnLogs?.sgstPercent || 0}%)
                  </div>
                  <div className="text-right">
                    ₹{selectedBill?.billTxnLogs?.sgst || 0}
                  </div>

                </>
              )}

              {/* Service Charges Section */}
              {selectedBill?.billTxnLogs?.service && 
               selectedBill?.billTxnLogs?.serviceAmount > 0 && (
                <>
                  <div className="col-span-2 font-medium border-b pb-1 mb-1 mt-3">
                    Additional Charges
                  </div>

                  <div className="text-gray-600">
                    Service Charge ({selectedBill?.billTxnLogs?.servicePercentage || 0}%)
                  </div>
                  <div className="text-right text-purple-600">
                    ₹{Number.parseFloat(
                      selectedBill?.billTxnLogs?.serviceAmount
                    ).toFixed(2)}
                  </div>
                </>
              )}

              <div className="col-span-2 mt-2">
                <div className="flex justify-between font-medium text-base bg-gray-50 p-2 rounded">
                  <span>Net Amount</span>
                  <span>₹{Number(netAmount)}</span>
                </div>
              </div>

              {/* Tips Section */}
              {selectedBill?.billTxnLogs?.tip > 0 && (
                <div className="col-span-2 mt-2">
                  <div className="flex justify-between text-sm text-gray-600">
                    <span>Tips</span>
                    <span>₹{selectedBill?.billTxnLogs?.tip}</span>
                  </div>
                </div>
              )}

              {parseFloat(roundOff) !== 0.0 && (
                <div className="col-span-2 mt-2">
                  <div className="flex justify-between text-sm text-gray-600">
                    <span>Round off</span>
                    <span>₹{Number(roundOff)}</span>
                  </div>
                </div>
              )}

              {/* Total Section */}
              <div className="col-span-2 pt-2 mt-3">
                <div className="flex justify-between font-bold text-base bg-gray-50 p-2 rounded">
                  <span>Total Amount</span>
                  <span>₹{selectedBill?.billTxnLogs?.total}</span>
                </div>
                <div className="flex justify-between text-sm text-gray-500 mt-2">
                  <span>Payment Type</span>
                  <span className="capitalize">
                    {selectedBill?.billTxnLogs?.paymentType || "N/A"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 z-10 bg-white flex gap-2 p-4 border-t">
          <button
            className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-800 py-2 px-4 rounded transition-colors"
            onClick={handleClose}
          >
            Close
          </button>
          <button
            className="flex-1 bg-gray-800 hover:bg-gray-900 text-white py-2 px-4 rounded transition-colors"
            onClick={() => handleReceiptPrint()}
          >
            Print
          </button>
        </div>
      </div>
    </div>
  );
};

export default BillDetailsModal;
